import {useCallback, useEffect, useMemo, useState} from 'react';

import {BackendApi} from "../../../../../api/api.ts";
import {User} from "../../../../../api/accessControl/User.ts";
import type {BackendAPITypes} from "../../../../../generated/blueprint.ts";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";

type TeammatesResponse = BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerTeammatesGetResponseBody;
type TeammateMember = TeammatesResponse['members'][number];
type PendingTeammateInvitation = TeammatesResponse['pending_invitations'][number];

export function useTeammates(currentUser: User) {
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser]);
    const {dispatch} = useNotifications();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [members, setMembers] = useState<TeammateMember[]>([]);
    const [pendingInvitations, setPendingInvitations] = useState<PendingTeammateInvitation[]>([]);
    const [hasLoadError, setHasLoadError] = useState<boolean>(false);

    const reload = useCallback(async () => {
        setIsLoading(true);
        setHasLoadError(false);
        try {
            const response = await api.Partner__teammates({});
            setMembers(response.members);
            setPendingInvitations(response.pending_invitations);
        } catch (error) {
            console.error('Error loading teammates:', error);
            setHasLoadError(true);
            dispatch({title: 'Fout', description: 'Kon de teamleden niet laden.', type: 'error'});
        } finally {
            setIsLoading(false);
        }
    }, [api, dispatch]);

    useEffect(() => {
        reload();
    }, [reload]);

    const inviteTeammate = useCallback(async (email: string): Promise<boolean> => {
        try {
            await api.Partner__inviteTeammate({email});
            dispatch({title: 'Uitnodiging verstuurd', description: `${email} is uitgenodigd.`, type: 'success'});
            await reload();
            return true;
        } catch (error) {
            console.error('Error inviting teammate:', error);
            dispatch({title: 'Fout', description: 'Het uitnodigen is mislukt. Probeer het opnieuw.', type: 'error'});
            return false;
        }
    }, [api, dispatch, reload]);

    const resendInvitation = useCallback(async (email: string): Promise<boolean> => {
        try {
            await api.Partner__inviteTeammate({email});
            dispatch({title: 'Uitnodiging opnieuw verstuurd', description: `Naar ${email}.`, type: 'success'});
            await reload();
            return true;
        } catch (error) {
            console.error('Error resending invitation:', error);
            dispatch({title: 'Fout', description: 'Opnieuw versturen is mislukt.', type: 'error'});
            return false;
        }
    }, [api, dispatch, reload]);

    return {isLoading, hasLoadError, members, pendingInvitations, reload, inviteTeammate, resendInvitation};
}
